/**
 * PHARMAKON — navigation model (design/ux/01_foundations.md §6.6-§6.12, WP-18;
 * D-21, D-22, A-17, A-20; AUD-NAV-01…-05, -08…-12, -14, -17, -18).
 *
 *  · NAV_TREE — the PROPRIETAIRE / MANAGER menu: 9 rail categories (OP-F-01),
 *    a flyout of sub-categories, a flyout of pages. Rendered by
 *    components/layout/nav/ (RailNav, NavDrawer) after `selectNavForUser`
 *    (lib/nav-tree.js) filtered it for the session.
 *  · CAISSIER_NAV — the short flat menu of the CAISSIER (A-20, §6.9).
 *  · NAVIGATION.ADMIN_PHARMAKON — the platform menu, unchanged, rendered flat (§6.11).
 *  · NAV_PAGE_TO_PATH / pageToPath — legacy page keys still used by
 *    `goPage` callers (Topbar notifications, Dashboards, useNav).
 *
 * Node shape (§6.6):
 *   id          stable dotted id « achats.factures.journal »
 *   label       menu label == page h1 (AUD-NAV-18)
 *   shortLabel  level 1 only, compact rail (≤ 11 characters)
 *   title       window title / palette label when it differs from `label`
 *   icon        lucide name — required on level 1 (and on flat items)
 *   to          leaf route (absent on containers)
 *   match       extra route patterns that make the leaf « current »
 *   children    containers only
 *   roles       optional override of ROUTE_ACCESS[to]
 *   menuRoles   narrower menu visibility than the route (OP-F-09)
 *   permission  [resource, action] for can() (config/permissions.config.js)
 *   module      module code → dynamic lock, never hides (OP-F-10)
 *   keywords    palette synonyms (French)
 *   badge       key of a count published in stores/navStore.js (optional)
 *   separatorBefore  a separator line above the item in its panel
 *   status      "live" | "planned" — a planned leaf is NEVER rendered (D-22);
 *               it flips to "live" in the commit that adds its route to
 *               App.tsx and ROUTE_ACCESS (§6.12)
 *   phase       programme phase that ships a planned page
 *   pageKey     legacy NAV_PAGE_TO_PATH key of the same page
 *
 * Reservations for the parallel programmes: the Trésorerie subtree of
 * tresorerie/design/ux/01_ia_navigation_cockpit.md §2.2 (nine planned leaves,
 * phase TRESO) REPLACES the four live Trésorerie leaves below in the commit
 * that ships the first Trésorerie page (TD-59 (a)) — the unique-route rule of
 * the tree test forces the `/tresorerie` leaf out at that moment. The
 * cross-module programme adds no menu page (its screens extend existing
 * pages); `/emplacements` stays a leaf until XWP-16 redirects it.
 */

/** Leaf status (§6.6). */
export const NAV_STATUS = Object.freeze({ LIVE: "live", PLANNED: "planned" });

const LIVE = NAV_STATUS.LIVE;
const PLANNED = NAV_STATUS.PLANNED;
const P = "PROPRIETAIRE";
const M = "MANAGER";
const AIDE_DECISION = "AIDE_DECISION";

/** Programme phases that ship the planned pages (§6.12 « Phase key »). */
export const NAV_PHASES = Object.freeze({
  ASSIST: "ASSIST",
  REAPPRO: "REAPPRO",
  ACHJD: "ACHJD",
  BC: "BC",
  BR: "BR",
  RECL: "RECL",
  REGL: "REGL",
  CHIFA: "CHIFA",
  TRESO: "TRESO",
});
const ph = NAV_PHASES;

/** @type {ReadonlyArray<any>} §6.7 — PROPRIETAIRE tree (MANAGER = the same, filtered, §6.8). */
export const NAV_TREE = [
  // ── 1 · Tableau de bord (level-1 leaf) ─────────────────────────────────────
  {
    id: "dashboard",
    label: "Tableau de bord",
    shortLabel: "Accueil",
    icon: "layout-dashboard",
    to: "/dashboard",
    status: LIVE,
    pageKey: "dashboard",
    keywords: ["accueil", "tableau de bord", "indicateurs"],
  },

  // ── 2 · Achats (A-17: the decision screens under Assistant de commande) ─────
  {
    id: "achats",
    label: "Achats",
    shortLabel: "Achats",
    icon: "truck",
    children: [
      {
        id: "achats.assistant",
        label: "Assistant de commande",
        children: [
          {
            id: "achats.assistant.generale",
            label: "Assistant de commande générale",
            to: "/achats/assistant-commande",
            status: PLANNED,
            phase: ph.ASSIST,
            keywords: ["commande", "suggestion", "panier"],
          },
          {
            id: "achats.assistant.reappro",
            label: "Réapprovisionnement",
            to: "/achats/reapprovisionnement",
            status: PLANNED,
            phase: ph.REAPPRO,
            keywords: ["qte min", "réassort"],
          },
          {
            id: "achats.assistant.decision",
            label: "Aide à la décision",
            to: "/decision",
            module: AIDE_DECISION,
            match: [
              "/aide-decision/config",
              "/aide-decision/pre-commandes/:id/comparison",
              "/aide-decision/offres/:id/counter",
            ],
            status: LIVE,
            pageKey: "decision",
            keywords: ["vélocité", "stock mort", "marge"],
          },
          {
            id: "achats.assistant.rotation",
            label: "État de rotation",
            to: "/decision/etat-rotation",
            status: LIVE,
            pageKey: "etat-rotation",
            keywords: ["rotation", "ABC", "couverture"],
          },
          {
            id: "achats.assistant.besoin",
            label: "Besoin en stock",
            to: "/decision/besoin",
            module: AIDE_DECISION,
            status: LIVE,
            keywords: ["besoin", "demande"],
          },
          {
            id: "achats.assistant.offres",
            label: "Offres reçues",
            to: "/decision/offres",
            module: AIDE_DECISION,
            status: LIVE,
            keywords: ["offre", "proposition fournisseur"],
          },
          {
            id: "achats.assistant.offres-historique",
            label: "Historique des offres",
            to: "/decision/offres/historique",
            module: AIDE_DECISION,
            status: LIVE,
          },
          {
            id: "achats.assistant.reglages",
            label: "Réglages du réapprovisionnement",
            to: "/commercial/reapprovisionnement",
            status: LIVE,
            pageKey: "commercial-reappro",
            keywords: ["quantités conseillées", "réapprovisionnement"],
          },
        ],
      },
      {
        id: "achats.factures",
        label: "Factures d'achat",
        children: [
          {
            id: "achats.factures.ajouter",
            label: "Ajouter",
            title: "Nouvel achat",
            to: "/achats/nouveau",
            status: LIVE,
            keywords: ["facture", "FA", "nouvelle facture", "nouvel achat", "saisir un achat"],
          },
          {
            id: "achats.factures.journal",
            label: "Journal des achats",
            to: "/achats",
            match: ["/achats/:id", "/achats/:id/modifier"],
            status: LIVE,
            pageKey: "achats",
            keywords: ["factures d'achat", "achats", "FA"],
          },
          {
            id: "achats.factures.journal-detaille",
            label: "Journal détaillé des achats",
            to: "/achats/journal-detaille",
            status: LIVE,
            keywords: ["lignes d'achat", "achats par produit", "détail des achats", "prix d'achat"],
          },
          {
            id: "achats.factures.efactures",
            label: "E-factures reçues",
            to: "/efactures-recu",
            match: ["/efactures-recu/:id", "/achat/efacture/:id/workspace"],
            status: LIVE,
            pageKey: "efactures",
            keywords: ["e-facture", "facture électronique", "dématérialisée"],
          },
        ],
      },
      {
        id: "achats.avoirs",
        label: "Avoirs fournisseur",
        children: [
          {
            id: "achats.avoirs.nouveau",
            label: "Nouvel avoir fournisseur",
            to: "/avoirs/nouveau",
            status: LIVE,
            keywords: ["avoir", "note de crédit", "ristourne", "écart"],
          },
          {
            id: "achats.avoirs.journal",
            label: "Journal des avoirs fournisseur",
            to: "/avoirs",
            match: ["/avoirs/:id"],
            status: LIVE,
            pageKey: "avoirs",
            keywords: ["avoirs"],
          },
        ],
      },
      {
        id: "achats.bc",
        label: "Bons de commande",
        children: [
          // Refonte WP-33 (ux/03 §3.3.1) — live with the editor.
          {
            id: "achats.bc.nouveau",
            label: "Nouveau bon de commande",
            to: "/commercial/bons-commande/nouveau",
            status: LIVE,
            keywords: ["BC", "commande", "commander"],
          },
          {
            id: "achats.bc.journal",
            label: "Journal des bons de commande",
            to: "/commercial/bons-commande",
            match: ["/commercial/bons-commande/:id"],
            status: LIVE,
            pageKey: "commercial-bons-commande",
            keywords: ["BC", "commande ferme"],
          },
          {
            id: "achats.bc.precommandes",
            label: "Pré-commandes à confirmer",
            to: "/commercial/precommandes-a-confirmer",
            status: LIVE,
            pageKey: "commercial-precommandes-confirmer",
            keywords: ["délégué", "à confirmer"],
          },
        ],
      },
      {
        id: "achats.bons-retour",
        label: "Bons de retour",
        children: [
          // Refonte WP-31 (D-22, ux/03 §4) — live with their routes.
          {
            id: "achats.bons-retour.nouveau",
            label: "Nouveau bon de retour",
            to: "/achats/bons-retour/nouveau",
            status: LIVE,
            keywords: ["retour fournisseur", "RF", "cassé", "périmé", "erreur de livraison"],
          },
          {
            id: "achats.bons-retour.journal",
            label: "Journal des bons de retour",
            to: "/achats/bons-retour",
            match: ["/achats/bons-retour/:id"],
            status: LIVE,
            keywords: ["bons de retour", "retours fournisseur", "RF"],
          },
        ],
      },
      {
        id: "achats.appels-offres",
        label: "Appels d'offres",
        to: "/commercial/appel-offres",
        module: AIDE_DECISION,
        match: ["/commercial/appel-offres/:id", "/commercial/appel-offres/:id/comparaison"],
        status: LIVE,
        pageKey: "commercial-appel-offres",
        keywords: ["RFQ", "appel d'offre", "consultation"],
      },
      {
        id: "achats.reclamations",
        label: "Réclamations",
        children: [
          // Refonte WP-35 (D-20, ux/03 §5) — live with their routes.
          {
            id: "achats.reclamations.nouvelle",
            label: "Nouvelle réclamation",
            to: "/achats/reclamations/nouvelle",
            status: LIVE,
            keywords: ["réclamation fournisseur", "manquant", "cassé", "périmé", "litige", "REC"],
          },
          {
            id: "achats.reclamations.journal",
            label: "Journal des réclamations",
            to: "/achats/reclamations",
            match: ["/achats/reclamations/:id"],
            status: LIVE,
            keywords: ["réclamations", "litiges fournisseur", "REC"],
          },
        ],
      },
      {
        id: "achats.urgent",
        label: "Commandes urgentes",
        children: [
          {
            id: "achats.urgent.directe",
            label: "Commande urgente directe",
            to: "/achat/urgence",
            status: LIVE,
            pageKey: "achat-urgence",
            keywords: ["achat urgent", "urgence"],
          },
          {
            id: "achats.urgent.precommande",
            label: "Pré-commande urgente",
            to: "/commercial/achat-urgent",
            status: LIVE,
            pageKey: "commercial-achat-urgent",
            keywords: ["achat urgent", "urgence"],
          },
        ],
      },
      {
        id: "achats.manquants",
        label: "Manquants",
        children: [
          {
            id: "achats.manquants.journal",
            label: "Journal des manquants",
            to: "/manquants",
            status: LIVE,
            pageKey: "manquants",
            keywords: ["rupture", "manquant"],
          },
          {
            id: "achats.manquants.assistant",
            label: "Assistant manquants",
            to: "/manquants/assistant-commande",
            status: LIVE,
            keywords: ["commande manquants"],
          },
          {
            id: "achats.manquants.secours",
            label: "Ventes de secours",
            to: "/secours",
            status: LIVE,
            pageKey: "secours",
            keywords: ["secours", "dette de lot"],
          },
        ],
      },
      {
        id: "achats.fournisseurs",
        label: "Fournisseurs",
        children: [
          {
            id: "achats.fournisseurs.liste",
            label: "Liste des fournisseurs",
            to: "/fournisseurs",
            match: ["/fournisseurs/:id"],
            status: LIVE,
            pageKey: "fournisseurs",
            keywords: ["fournisseur", "distributeur", "grossiste"],
          },
          {
            id: "achats.fournisseurs.nouveau",
            label: "Nouveau fournisseur",
            to: "/fournisseurs/nouveau",
            status: LIVE,
          },
          {
            id: "achats.fournisseurs.catalogue",
            label: "Catalogue fournisseur",
            to: "/commercial/catalogue",
            status: LIVE,
            pageKey: "commercial-catalogue",
            keywords: ["catalogue", "tarif"],
          },
          {
            id: "achats.fournisseurs.livraisons",
            label: "Livraisons entrantes",
            to: "/livraisons/incoming",
            status: LIVE,
            pageKey: "commercial-livraisons",
            keywords: ["colis", "transport", "BL"],
          },
          // Trésorerie TWP-28 (TD-131) — « Mon relevé de compte » is retired: /mon-releve redirects to
          // « Trésorerie › Règlements › Soldes fournisseurs », the relevé being each fournisseur's drawer.
          {
            // Reserved for the refonte WP-53 (OP-F-15) and never rendered: /achats/reglements redirects to the
            // one page « Trésorerie › Règlements » (TO-02, TD-51); the menu keeps the Trésorerie leaf only
            // (TD-132) until the owner settles OP-F-15 against TD-132 (TD-333).
            id: "achats.fournisseurs.reglements",
            label: "Règlements fournisseurs",
            to: "/achats/reglements",
            status: PLANNED,
            phase: ph.REGL,
          },
        ],
      },
    ],
  },

  // ── 3 · Ventes ─────────────────────────────────────────────────────────────
  {
    id: "ventes",
    label: "Ventes",
    shortLabel: "Ventes",
    icon: "shopping-cart",
    children: [
      {
        id: "ventes.comptoir",
        label: "Comptoir",
        children: [
          {
            id: "ventes.comptoir.libre",
            label: "Vente libre",
            to: "/caisse",
            status: LIVE,
            pageKey: "vente-libre",
            keywords: ["caisse", "comptoir", "vente"],
          },
          {
            id: "ventes.comptoir.chifa",
            label: "Vente CHIFA",
            to: "/chifa",
            status: LIVE,
            pageKey: "vente-chifa",
            keywords: ["chifa", "CNAS", "carte"],
          },
        ],
      },
      {
        id: "ventes.journal",
        label: "Journal des ventes",
        children: [
          {
            id: "ventes.journal.liste",
            label: "Journal des ventes",
            to: "/ventes",
            status: LIVE,
            pageKey: "ventes",
            keywords: ["ventes", "tickets"],
          },
        ],
      },
      {
        id: "ventes.retours",
        label: "Retours clients",
        children: [
          {
            id: "ventes.retours.nouveau",
            label: "Nouveau retour",
            to: "/retours/nouveau",
            status: LIVE,
            keywords: ["retour client", "échange"],
          },
          {
            id: "ventes.retours.journal",
            label: "Journal des retours",
            to: "/retours",
            match: ["/retours/:id"],
            status: LIVE,
            pageKey: "retours",
          },
        ],
      },
      {
        id: "ventes.clients",
        label: "Clients",
        children: [
          {
            id: "ventes.clients.liste",
            label: "Liste des clients",
            to: "/clients",
            match: ["/clients/:id", "/clients/:id/edit"],
            status: LIVE,
            pageKey: "clients",
            keywords: ["client", "patient"],
          },
          {
            id: "ventes.clients.nouveau",
            label: "Nouveau client",
            to: "/clients/nouveau",
            status: LIVE,
          },
          {
            id: "ventes.clients.credits",
            label: "Crédits clients",
            to: "/credits",
            match: ["/credits/:id"],
            status: LIVE,
            pageKey: "credit",
            keywords: ["crédit", "dette client"],
          },
          {
            id: "ventes.clients.fidelite",
            label: "Fidélité",
            to: "/fidelite",
            status: LIVE,
            pageKey: "fidelite",
            keywords: ["points", "fidélité"],
          },
        ],
      },
      {
        id: "ventes.facturation",
        label: "Facturation clients",
        children: [
          {
            id: "ventes.facturation.hub",
            label: "Facturation",
            to: "/facturation",
            status: LIVE,
            pageKey: "facturation",
            keywords: ["facture client", "devis", "bon de livraison"],
          },
        ],
      },
      {
        // « label as today » (§6.7): the Aalaji brand name.
        id: "ventes.aalaji",
        label: "علاجي",
        children: [
          {
            id: "ventes.aalaji.hub",
            label: "علاجي",
            to: "/aalaji",
            status: LIVE,
            pageKey: "aalaji",
            keywords: ["aalaji", "alaji", "ailaji", "patients liés"],
          },
          {
            id: "ventes.aalaji.preparations",
            label: "Préparations",
            to: "/aalaji/preparations",
            status: LIVE,
            keywords: ["aalaji", "préparation", "commande patient"],
          },
          {
            id: "ventes.aalaji.parametres",
            label: "Paramètres علاجي",
            to: "/aalaji/parametres",
            status: LIVE,
            keywords: ["aalaji", "paramètres aalaji"],
          },
        ],
      },
      {
        id: "ventes.conseil",
        label: "Aide au Conseil",
        children: [
          {
            id: "ventes.conseil.session",
            label: "Aide au Conseil",
            to: "/dermo-conseiller",
            match: ["/conseil"],
            status: LIVE,
            pageKey: "dermo-conseiller",
            keywords: ["conseil", "dermo", "dermocosmétique"],
          },
        ],
      },
    ],
  },

  // ── 4 · Stock ──────────────────────────────────────────────────────────────
  {
    id: "stock",
    label: "Stock",
    shortLabel: "Stock",
    icon: "boxes",
    children: [
      {
        id: "stock.lots",
        label: "Stock & lots",
        children: [
          {
            id: "stock.lots.dashboard",
            label: "Stock & lots",
            to: "/stock",
            status: LIVE,
            pageKey: "stock",
            keywords: ["stock", "lots", "alertes stock", "inventaire"],
          },
          {
            id: "stock.lots.corrections",
            label: "Corrections de stock",
            to: "/stock/corrections",
            status: LIVE,
            keywords: ["correction", "ajustement", "inventaire"],
          },
          {
            id: "stock.lots.peremption",
            label: "Risque de péremption",
            to: "/stock/risque-peremption",
            status: LIVE,
            pageKey: "risque-peremption",
            keywords: ["péremption", "expiration", "périmés"],
          },
        ],
      },
      {
        id: "stock.produits",
        label: "Produits",
        children: [
          {
            id: "stock.produits.liste",
            label: "Liste des produits",
            to: "/produits",
            match: ["/produits/:id"],
            status: LIVE,
            pageKey: "produits",
            keywords: ["produit", "catalogue", "fiche produit"],
          },
          {
            id: "stock.produits.nouveau",
            label: "Nouveau produit",
            to: "/produits/nouveau",
            status: LIVE,
          },
          {
            id: "stock.produits.recherche",
            label: "Recherche produit",
            to: "/recherche-produits",
            status: LIVE,
            pageKey: "recherche-produits",
            keywords: ["rechercher un produit", "disponibilité"],
          },
        ],
      },
      {
        id: "stock.magasins",
        label: "Magasins & emplacements",
        children: [
          {
            id: "stock.magasins.liste",
            label: "Magasins",
            to: "/magasins",
            status: LIVE,
            pageKey: "magasins",
            keywords: ["magasin", "dépôt", "réserve"],
          },
          {
            id: "stock.magasins.emplacements",
            label: "Emplacements",
            to: "/emplacements",
            status: LIVE,
            pageKey: "emplacements",
            keywords: ["emplacement", "rayon", "étagère"],
          },
        ],
      },
      {
        id: "stock.confreres",
        label: "Mes confrères",
        children: [
          {
            id: "stock.confreres.hub",
            label: "Mes confrères",
            to: "/confreres",
            status: LIVE,
            pageKey: "confreres",
            keywords: ["confrère", "transfert inter-officines", "réseau"],
          },
        ],
      },
    ],
  },

  // ── 5 · Trésorerie ─────────────────────────────────────────────────────────
  {
    id: "tresorerie",
    label: "Trésorerie",
    shortLabel: "Trésorerie",
    icon: "wallet",
    children: [
      // Live today (§6.7) — replaced by the TRESO subtree below when it ships. TWP-22 (R-09, RC-131):
      // the owner / manager « Caisses › Encaisser / Décaisser » leaf is gone — `/ma-tresorerie` is the
      // caissier's own journal and redirects P / M to « Mouvements » (the officine journal).
      {
        id: "tresorerie.journal",
        label: "Mouvements de trésorerie",
        children: [
          {
            id: "tresorerie.journal.mouvements",
            label: "Mouvements de trésorerie",
            title: "Trésorerie",
            to: "/tresorerie",
            status: LIVE,
            pageKey: "tresorerie",
            keywords: ["trésorerie", "mouvements", "journal de caisse"],
          },
        ],
      },
      {
        id: "tresorerie.centrale",
        label: "Caisse centrale",
        children: [
          {
            id: "tresorerie.centrale.compte",
            label: "Caisse centrale",
            to: "/caisse-centrale",
            status: LIVE,
            pageKey: "caisse-centrale",
            keywords: ["caisse centrale", "charges", "fonds"],
          },
        ],
      },
      // Reserved — Trésorerie IA §2.2 (direct links, no level 3, separators).
      {
        id: "tresorerie.vue",
        label: "Vue d'ensemble",
        icon: "gauge",
        to: "/tresorerie",
        status: PLANNED,
        phase: ph.TRESO,
        keywords: ["cockpit", "liquidité", "trésorerie", "combien d'argent"],
      },
      {
        // TWP-18 — live: the tree of money locations and its « Sessions de caisse » tab (/caisses redirects here).
        id: "tresorerie.comptes",
        label: "Comptes & caisses",
        icon: "landmark",
        to: "/tresorerie/comptes",
        match: ["/tresorerie/comptes/sessions", "/tresorerie/comptes/*"],
        separatorBefore: true,
        status: LIVE,
        pageKey: "comptes-caisses",
        keywords: ["compte bancaire", "caisse centrale", "caisse virtuelle", "comptoir", "sessions", "sessions de caisse", "clôture", "journée"],
      },
      {
        // TWP-20 — live: the operations list (views, « À confirmer »), the one movement form and the
        // operation drawer; TWP-29 adds the full journal (filters, perimeter, export, print).
        id: "tresorerie.mouvements",
        label: "Mouvements",
        icon: "arrow-left-right",
        to: "/tresorerie/mouvements",
        match: ["/tresorerie/mouvements/*"],
        badge: "tresorerie.brouillons",
        status: LIVE,
        pageKey: "mouvements-tresorerie",
        keywords: ["journal", "encaissement", "décaissement", "transfert", "à confirmer", "brouillon", "opération"],
      },
      {
        // TWP-26 — live: G1 « Soldes fournisseurs », G3 « Journal des règlements », the settlement form (G2) and
        // the fournisseur / facture drawers (T2 / G6); « Charges » joins with TWP-27.
        id: "tresorerie.reglements",
        label: "Règlements",
        icon: "hand-coins",
        to: "/tresorerie/reglements",
        match: ["/tresorerie/reglements/:onglet", "/tresorerie/reglements/*"],
        status: LIVE,
        pageKey: "reglements-tresorerie",
        keywords: [
          "règlement fournisseur",
          "règlements fournisseurs",
          "régler",
          "payer une facture",
          "relevé",
          "mon relevé de compte",
          "soldes fournisseurs",
          "dettes fournisseurs",
          "journal des règlements",
          "paiements enregistrés par vos fournisseurs",
        ],
      },
      {
        id: "tresorerie.recurrents",
        label: "Récurrents",
        icon: "repeat",
        to: "/tresorerie/recurrents",
        match: ["/tresorerie/recurrents/*"],
        badge: "tresorerie.recurrents",
        status: PLANNED,
        phase: ph.TRESO,
        keywords: ["loyer", "électricité", "abonnement", "prélèvement"],
      },
      {
        id: "tresorerie.tiers",
        label: "Tiers",
        icon: "users",
        to: "/tresorerie/tiers",
        match: ["/tresorerie/tiers/*"],
        separatorBefore: true,
        status: PLANNED,
        phase: ph.TRESO,
        keywords: ["fournisseur", "client", "CNAS", "solde tiers"],
      },
      {
        id: "tresorerie.echeances",
        label: "Dettes & échéances",
        icon: "calendar-clock",
        to: "/tresorerie/echeances",
        match: ["/tresorerie/echeances/*"],
        status: PLANNED,
        phase: ph.TRESO,
        keywords: ["dette", "échéance", "à payer", "créance"],
      },
      {
        id: "tresorerie.analyses",
        label: "Analyses de trésorerie",
        icon: "area-chart",
        to: "/tresorerie/analyses",
        match: ["/tresorerie/analyses/*", "/tresorerie/analytics/*"],
        separatorBefore: true,
        badge: "tresorerie.anomalies",
        status: PLANNED,
        phase: ph.TRESO,
        keywords: ["analytics", "flux", "cash-flow", "comparaison", "N−1", "anomalie", "à vérifier"],
      },
      {
        // TWP-23 — live (TD-202): the « Trésorerie » section of the one Paramètres workspace (WP-20).
        id: "tresorerie.parametres",
        label: "Paramètres de trésorerie",
        icon: "sliders-horizontal",
        to: "/tresorerie/parametres",
        match: ["/tresorerie/parametres/:onglet", "/tresorerie/parametres/*"],
        separatorBefore: true,
        status: LIVE,
        pageKey: "parametres-tresorerie",
        keywords: ["motifs", "seuils", "paramètres caisse", "tolérance d'écart", "fonds de caisse", "comptage à l'aveugle", "signaux", "horizon"],
      },
    ],
  },

  // ── 6 · CNAS / CHIFA (OP-F-14) ─────────────────────────────────────────────
  {
    id: "chifa",
    label: "CNAS / CHIFA",
    shortLabel: "CHIFA",
    icon: "contact",
    children: [
      {
        id: "chifa.instances",
        label: "Instances CHIFA",
        children: [
          {
            id: "chifa.instances.nouvelle",
            label: "Nouvelle instance",
            to: "/instances-chifa/nouveau",
            status: LIVE,
            keywords: ["instance à terme", "chifa"],
          },
          {
            id: "chifa.instances.liste",
            label: "Instances à terme",
            to: "/instances-chifa",
            // X XWP-21 — the journal with an instance's drawer open
            match: ["/instances-chifa/:id"],
            status: LIVE,
            pageKey: "instance-chifa",
            keywords: ["instance", "chifa"],
          },
        ],
      },
      {
        id: "chifa.bordereaux",
        label: "Bordereaux CHIFA",
        children: [
          {
            id: "chifa.bordereaux.journal",
            label: "Journal des bordereaux CHIFA",
            title: "Suivi des bordereaux",
            to: "/bordereaux-chifa",
            match: ["/bordereaux-chifa/:id"],
            status: PLANNED,
            phase: ph.CHIFA,
          },
          {
            id: "chifa.bordereaux.import",
            label: "Importation / vérification",
            title: "Importation / vérification d'un bordereau",
            to: "/bordereaux-chifa/import",
            status: PLANNED,
            phase: ph.CHIFA,
          },
        ],
      },
    ],
  },

  // ── 7 · Conformité ─────────────────────────────────────────────────────────
  {
    id: "conformite",
    label: "Conformité",
    shortLabel: "Conformité",
    icon: "shield-check",
    children: [
      {
        id: "conformite.psychotropes",
        label: "Psychotropes",
        children: [
          {
            id: "conformite.psychotropes.ordonnancier",
            label: "Ordonnancier",
            to: "/ordonnancier",
            // X XWP-55 — the delivery drawer /ordonnancier/:id is the register with that entry open
            match: ["/ordonnancier/:id"],
            status: LIVE,
            pageKey: "ordonnancier",
            keywords: ["psychotrope", "registre"],
          },
          {
            id: "conformite.psychotropes.annexe12",
            label: "Annexe 12",
            to: "/ordonnancier/annexe-12",
            status: LIVE,
          },
          {
            id: "conformite.psychotropes.annexe13",
            label: "Annexe 13",
            to: "/ordonnancier/annexe-13",
            status: LIVE,
          },
        ],
      },
      {
        id: "conformite.medecins",
        label: "Médecins",
        children: [
          {
            id: "conformite.medecins.liste",
            label: "Médecins",
            to: "/medecins",
            status: LIVE,
            pageKey: "medecins",
            keywords: ["médecin", "prescripteur"],
          },
        ],
      },
    ],
  },

  // ── 8 · Analyses ───────────────────────────────────────────────────────────
  {
    id: "analyses",
    label: "Analyses",
    shortLabel: "Analyses",
    icon: "line-chart",
    children: [
      {
        id: "analyses.analytics",
        label: "Analytics",
        children: [
          {
            id: "analyses.analytics.bi",
            label: "Analytics",
            to: "/analytics",
            match: ["/analytics/medecins/:id"],
            status: LIVE,
            pageKey: "analytics",
            keywords: ["statistiques", "chiffre d'affaires", "BI"],
          },
        ],
      },
      {
        id: "analyses.rapports",
        label: "Rapports financiers",
        children: [
          {
            id: "analyses.rapports.finance",
            label: "Rapports financiers",
            to: "/rapports-finance",
            status: LIVE,
            pageKey: "rapports-finance",
            keywords: ["zakat", "créances", "rapport"],
          },
        ],
      },
    ],
  },

  // ── 9 · Administration (OP-F-09: MANAGER menu as today) ─────────────────────
  {
    id: "admin",
    label: "Administration",
    shortLabel: "Admin.",
    icon: "settings",
    children: [
      {
        id: "admin.officine",
        label: "Officine",
        children: [
          {
            id: "admin.officine.parametres",
            label: "Paramètres",
            to: "/parametres",
            // The one settings workspace (WP-20): its sections and their onglets are
            // sub-routes (config/parametres.config.js) and keep Paramètres current.
            match: ["/parametres/:section", "/parametres/:section/:onglet"],
            menuRoles: [P],
            status: LIVE,
            pageKey: "parametres",
            keywords: ["réglages", "paramètres de la pharmacie", "tolérance", "étiquettes"],
          },
          {
            id: "admin.officine.equipe",
            label: "Équipe & utilisateurs",
            to: "/equipe",
            menuRoles: [P],
            status: LIVE,
            pageKey: "equipe",
            keywords: ["équipe", "utilisateurs", "caissier", "employé"],
          },
          {
            id: "admin.officine.abonnement",
            label: "Abonnement & paiements",
            to: "/abonnement",
            menuRoles: [P],
            status: LIVE,
            pageKey: "abonnement",
            keywords: ["abonnement", "licence", "modules", "paiement"],
          },
        ],
      },
      {
        id: "admin.controle",
        label: "Contrôle",
        children: [
          {
            id: "admin.controle.audit",
            label: "Journal d'audit",
            to: "/audit",
            menuRoles: [P],
            status: LIVE,
            pageKey: "audit",
            keywords: ["audit", "historique des actions"],
          },
          {
            id: "admin.controle.sync",
            label: "Synchronisation à vérifier",
            to: "/sync/a-verifier",
            menuRoles: [P, M],
            status: LIVE,
            keywords: ["hors ligne", "conflits", "synchronisation"],
          },
        ],
      },
      {
        id: "admin.assistance",
        label: "Assistance",
        children: [
          {
            id: "admin.assistance.support",
            label: "Support",
            to: "/support",
            menuRoles: [P, M],
            status: LIVE,
            pageKey: "support",
            keywords: ["ticket", "aide"],
          },
          {
            id: "admin.assistance.compte",
            label: "Mon compte",
            to: "/moncompte",
            menuRoles: [P, M],
            status: LIVE,
            pageKey: "moncompte",
            keywords: ["profil", "mot de passe"],
          },
        ],
      },
    ],
  },
];

/**
 * §6.9 — the CAISSIER menu: flat, the content of the pre-refonte menu minus
 * Showcase; « Ma caisse » now points at the CAISSIER home `/caissier`
 * (AUD-NAV-14). Groups are rendered as headings, items as rail links.
 */
export const CAISSIER_NAV = [
  {
    id: "caissier.caisse",
    label: "Caisse",
    children: [
      { id: "caissier.caisse.ma-caisse", icon: "banknote", label: "Ma caisse", to: "/caissier", status: LIVE, keywords: ["accueil", "caisse"] },
      { id: "caissier.caisse.libre", icon: "shopping-bag", label: "Vente libre", to: "/caisse", status: LIVE, pageKey: "vente-libre", keywords: ["vente", "comptoir"] },
      { id: "caissier.caisse.chifa", icon: "badge-check", label: "Vente CHIFA", to: "/chifa", status: LIVE, pageKey: "vente-chifa", keywords: ["chifa", "CNAS"] },
      // Trésorerie TWP-22 (TD-176) — the caissier's journal now holds his transfers too: « Mouvements de ma caisse ».
      { id: "caissier.caisse.tresorerie", icon: "wallet", label: "Mouvements de ma caisse", to: "/ma-tresorerie", status: LIVE, pageKey: "ma-tresorerie", keywords: ["encaissement", "décaissement", "transfert", "ma caisse", "versement"] },
    ],
  },
  {
    id: "caissier.consultation",
    label: "Consultation",
    children: [
      { id: "caissier.consultation.ventes", icon: "list", label: "Mes ventes", to: "/ventes", status: LIVE, pageKey: "ventes", keywords: ["ventes", "tickets"] },
      { id: "caissier.consultation.clients", icon: "user-round", label: "Clients", to: "/clients", match: ["/clients/:id"], status: LIVE, pageKey: "clients", keywords: ["client", "patient"] },
      { id: "caissier.consultation.aalaji", icon: "heart-pulse", label: "علاجي", to: "/aalaji", match: ["/aalaji/preparations"], status: LIVE, pageKey: "aalaji", keywords: ["aalaji", "alaji", "ailaji"] },
      { id: "caissier.consultation.credit", icon: "hourglass", label: "Crédit", to: "/credits", match: ["/credits/:id"], status: LIVE, pageKey: "credit", keywords: ["crédit", "dette client"] },
      { id: "caissier.consultation.instances", icon: "hourglass", label: "Instance à terme", to: "/instances-chifa", match: ["/instances-chifa/nouveau", "/instances-chifa/:id"], status: LIVE, pageKey: "instance-chifa", keywords: ["instance", "chifa"] },
      { id: "caissier.consultation.retours", icon: "rotate-ccw", label: "Retours", to: "/retours", match: ["/retours/nouveau", "/retours/:id"], status: LIVE, pageKey: "retours", keywords: ["retour client"] },
      { id: "caissier.consultation.recherche", icon: "search", label: "Recherche produit", to: "/recherche-produits", status: LIVE, pageKey: "recherche-produits", keywords: ["rechercher un produit", "disponibilité"] },
    ],
  },
  {
    id: "caissier.outils",
    label: "Outils",
    children: [
      { id: "caissier.outils.conseil", icon: "sparkles", label: "Aide au Conseil", to: "/dermo-conseiller", match: ["/conseil"], status: LIVE, pageKey: "dermo-conseiller", keywords: ["conseil", "dermo"] },
      { id: "caissier.outils.compte", icon: "user", label: "Mon compte", to: "/moncompte", status: LIVE, pageKey: "moncompte", keywords: ["profil", "mot de passe"] },
    ],
  },
];

/**
 * §6.13 — routes of the shell that are deliberately NOT menu pages (each is
 * reached from inside another page, is a landing or a legacy page). The tree
 * test asserts that every route of App.tsx is a menu page, a `match` of one,
 * a redirect of ROUTE_REDIRECTS, or listed here with its reason.
 */
export const NAV_NON_MENU_ROUTES = Object.freeze([
  { route: "/caissier", reason: "CAISSIER home (its « Ma caisse »); P / M reach it by URL, not from their menu" },
  { route: "/paiement/succes", reason: "Chargily redirect after a checkout" },
  { route: "/paiement/echec", reason: "Chargily redirect after a checkout" },
  { route: "/admin", reason: "redirect to /admin/dashboard" },
  { route: "/admin/dashboard", reason: "ADMIN_PHARMAKON home (homeForRole); the admin menu's « Tableau de bord » opens /dashboard" },
  { route: "/admin/pharmacies/:id/licences", reason: "reached from Pharmacies, Licences, Modules, Facturation (admin)" },
  { route: "/admin/cnas/import", reason: "reached from Base CNAS" },
  { route: "/admin/produits-base/import", reason: "reached from Nomenclature" },
  { route: "/admin/aalaji/referentiels", reason: "platform page with no menu entry and no inbound link — the admin menu is kept unchanged (§6.11)" },
]);

/**
 * §6.11 — the platform menu (ADMIN_PHARMAKON), content unchanged, rendered
 * flat. The pre-refonte PROPRIETAIRE / MANAGER / CAISSIER / FOURNISSEUR
 * groups are gone: NAV_TREE and CAISSIER_NAV replace them; the FOURNISSEUR
 * one was dead config (AUD-NAV-17 — the portal has its own menu,
 * components/fournisseur/layout/fournisseur.nav.config.js).
 */
export const NAVIGATION = {
  ADMIN_PHARMAKON: [
    {
      type: "group",
      label: "Plan plateforme",
      items: [
        {
          id: "dashboard",
          icon: "layout-dashboard",
          label: "Tableau de bord",
          page: "dashboard",
        },
        {
          id: "comptes",
          icon: "user-check",
          label: "Comptes à valider",
          page: "comptes",
        },
        {
          id: "pharmacies",
          icon: "building-2",
          label: "Pharmacies",
          page: "pharmacies",
        },
        {
          id: "fournisseurs",
          icon: "building",
          label: "Fournisseurs",
          page: "fournisseurs-admin",
        },
      ],
    },
    // F-2 (B2G) — the three NOVERA macro-admin vertical consoles (Novanalyse /
    // Cabinets / Ailaji), siblings of the existing Pharmacies/Fournisseurs plane.
    {
      type: "group",
      label: "Verticales NOVERA",
      items: [
        {
          id: "novanalyse-console",
          icon: "flask-conical",
          label: "Novanalyse",
          page: "novanalyse-console",
        },
        {
          id: "cabinets-console",
          icon: "stethoscope",
          label: "Cabinets",
          page: "cabinets-console",
        },
        {
          id: "ailaji-console",
          icon: "heart-pulse",
          label: "Ailaji",
          page: "ailaji-console",
        },
      ],
    },
    {
      type: "group",
      label: "Données",
      items: [
        { id: "cnas", icon: "database", label: "Base CNAS", page: "cnas" },
        {
          id: "nomenclature",
          icon: "list-tree",
          label: "Nomenclature",
          page: "nomenclature",
        },
        {
          id: "modules",
          icon: "puzzle",
          label: "Modules",
          page: "modules-admin",
        },
      ],
    },
    {
      type: "group",
      label: "Opérations",
      items: [
        {
          id: "licences",
          icon: "key-round",
          label: "Licences",
          page: "licences",
        },
        {
          id: "facturation",
          icon: "receipt",
          label: "Facturation",
          page: "facturation-admin",
        },
        {
          id: "abonnements-fournisseurs",
          icon: "credit-card",
          label: "Abonnements ERP",
          page: "abonnements-fournisseurs",
        },
        {
          id: "pricing",
          icon: "tag",
          label: "Tarification",
          page: "pricing",
        },
        {
          id: "tickets",
          icon: "life-buoy",
          label: "Tickets support",
          page: "tickets",
        },
        {
          id: "broadcast",
          icon: "megaphone",
          label: "Broadcasts",
          page: "broadcast",
        },
        {
          id: "admin-notifications",
          icon: "bell",
          label: "Notifications",
          page: "admin-notifications",
        },
      ],
    },
    {
      type: "group",
      label: "Surveillance",
      items: [
        {
          id: "health",
          icon: "activity",
          label: "Santé système",
          page: "health",
        },
        {
          id: "errors",
          icon: "alert-triangle",
          label: "Erreurs",
          page: "errors",
        },
        {
          id: "security",
          icon: "shield",
          label: "Sécurité",
          page: "security",
        },
        {
          id: "admin-users",
          icon: "users",
          label: "Utilisateurs",
          page: "admin-users",
        },
        {
          id: "audit-explorer",
          icon: "scroll",
          label: "Explorateur audit",
          page: "audit-explorer",
        },
      ],
    },
  ],
};

/**
 * Command-palette VERBS (Trésorerie UX 01 §2.6, 03_UX_DESIGN §2.1; RC-132): actions, not pages. A verb
 * is offered only when its `leaf` is a page the session's menu shows (so a role never meets a verb it
 * cannot reach); it opens its form on the current page when that page hosts it (`hosts`), else on
 * `leaf`, through the page's `?<param>=<value>` (the page opens the form and drops the parameter).
 * The settlement, anomalies and comparison verbs join with their screens (TWP-26, TWP-39).
 */
export const PALETTE_VERBS = Object.freeze([
  Object.freeze({
    id: "tresorerie.nouvel-encaissement",
    label: "Nouvel encaissement",
    meta: "Trésorerie › Mouvements",
    leaf: "/tresorerie/mouvements",
    hosts: Object.freeze(["/tresorerie/mouvements", "/tresorerie/comptes"]),
    param: Object.freeze({ nouveau: "encaissement" }),
    icon: "arrow-down-left",
    keywords: ["encaisser", "recette", "entrée d'argent", "apport", "mouvement"],
  }),
  Object.freeze({
    id: "tresorerie.nouveau-decaissement",
    label: "Nouveau décaissement",
    meta: "Trésorerie › Mouvements",
    leaf: "/tresorerie/mouvements",
    hosts: Object.freeze(["/tresorerie/mouvements", "/tresorerie/comptes"]),
    param: Object.freeze({ nouveau: "decaissement" }),
    icon: "arrow-up-right",
    keywords: ["décaisser", "dépense", "sortie d'argent", "payer", "mouvement"],
  }),

  Object.freeze({
    id: "tresorerie.nouveau-transfert",
    label: "Nouveau transfert",
    meta: "Trésorerie › Mouvements",
    leaf: "/tresorerie/mouvements",
    hosts: Object.freeze(["/tresorerie/mouvements", "/tresorerie/comptes"]),
    param: Object.freeze({ nouveau: "transfert" }),
    icon: "arrow-left-right",
    keywords: ["transférer", "versement", "virement interne", "approvisionner", "mouvement"],
  }),
  Object.freeze({
    // TWP-26 (RC-132) — « Régler un fournisseur »: the one settlement form, opened by `?regler=1` on the leaf.
    id: "tresorerie.regler-fournisseur",
    label: "Régler un fournisseur",
    meta: "Trésorerie › Règlements",
    leaf: "/tresorerie/reglements",
    hosts: Object.freeze(["/tresorerie/reglements"]),
    param: Object.freeze({ regler: "1" }),
    icon: "hand-coins",
    keywords: ["régler", "payer un fournisseur", "règlement fournisseur", "payer une facture", "ventiler"],
  }),
  Object.freeze({
    // TWP-27 — « Nouvelle charge »: the charge form, opened by `?nouvelle_charge=1` on « Règlements » (the
    // page moves to its Charges tab). Every tab of the page hosts it (its parameters kept).
    id: "tresorerie.nouvelle-charge",
    label: "Nouvelle charge",
    meta: "Trésorerie › Règlements › Charges",
    leaf: "/tresorerie/reglements",
    hosts: Object.freeze(["/tresorerie/reglements", "/tresorerie/reglements/soldes", "/tresorerie/reglements/journal", "/tresorerie/reglements/charges"]),
    param: Object.freeze({ nouvelle_charge: "1" }),
    icon: "receipt-text",
    keywords: ["charge", "salaire", "loyer", "électricité", "sonelgaz", "impôt", "facture d'énergie"],
  }),
]);

/**
 * Maps the legacy page-key strings to react-router URLs. Kept for the
 * `goPage` / `useNav` callers and the notification `action.page` fallback
 * (§6.6 « Kept for compatibility »); `showcase` removed (A-20).
 */
export const NAV_PAGE_TO_PATH = {
  dashboard: "/dashboard",
  caisses: "/tresorerie/comptes/sessions",
  "comptes-caisses": "/tresorerie/comptes",
  "mouvements-tresorerie": "/tresorerie/mouvements",
  "parametres-tresorerie": "/tresorerie/parametres",
  "reglements-tresorerie": "/tresorerie/reglements",
  achats: "/achats",
  efactures: "/efactures-recu",
  avoirs: "/avoirs",
  stock: "/stock",
  // Batch 4 (Cluster D) — professional product search (P/M/C)
  "recherche-produits": "/recherche-produits",
  emplacements: "/emplacements",
  fournisseurs: "/fournisseurs",
  ventes: "/ventes",
  "vente-libre": "/caisse",
  "vente-chifa": "/chifa",
  produits: "/produits",
  clients: "/clients",
  // Phase 1 (XpertPharm parity) — psychotrope module
  ordonnancier: "/ordonnancier",
  medecins: "/medecins",
  // Phase 2 (XpertPharm parity) — shortage & reorder
  manquants: "/manquants",
  // Batch-1 vague 2 (Q2) — vieillissement des ventes de secours
  secours: "/secours",
  // Phase 4 (XpertPharm parity) — expiry risk
  "risque-peremption": "/stock/risque-peremption",
  // Phase 5 (XpertPharm parity) — loyalty programme
  fidelite: "/fidelite",
  // Phase 7 (XpertPharm parity) — sales-document lifecycle
  facturation: "/facturation",
  // Phase 8 (XpertPharm parity) — treasury & cash management
  tresorerie: "/tresorerie",
  // Cluster F (2026-06-24) — caissier encaissement / décaissement surface
  "ma-tresorerie": "/ma-tresorerie",
  // Batch-1 vague 3 (2026-08) — la caisse centrale d'officine
  "caisse-centrale": "/caisse-centrale",
  // Phase 11 (XpertPharm parity) — multi-warehouse
  magasins: "/magasins",
  // TIP (2026-08) — réseau de confrères / transferts inter-pharmacies
  confreres: "/confreres",
  // Phase 10 (XpertPharm parity) — financial reports
  "rapports-finance": "/rapports-finance",
  credit: "/credits",
  "instance-chifa": "/instances-chifa",
  retours: "/retours",
  // AUD-NAV-14 — the Aalaji hub had no page key (Dashboards goPath, notifications).
  aalaji: "/aalaji",
  // Deprecated alias — old "instance" page key now resolves to the renamed
  // Crédit page (the name "Instance à terme" is reused by the CHIFA feature).
  instance: "/credits",
  decision: "/decision",
  analytics: "/analytics",
  "etat-rotation": "/decision/etat-rotation",
  // E-Facture ERP module (2026-06)
  "achat-urgence": "/achat/urgence",
  // Module 03 (Commercial) — pharmacy-facing surfaces (2026-06)
  "commercial-catalogue": "/commercial/catalogue",
  "commercial-appel-offres": "/commercial/appel-offres",
  "commercial-bons-commande": "/commercial/bons-commande",
  "commercial-precommandes-confirmer": "/commercial/precommandes-a-confirmer",
  "commercial-achat-urgent": "/commercial/achat-urgent",
  "commercial-reappro": "/commercial/reapprovisionnement",
  // Module 05 (Logistics) — pharmacy "Incoming Deliveries" (E2)
  "commercial-livraisons": "/livraisons/incoming",
  // Parametric base paths — used with useNav(page, id) (the :id is appended).
  "efacture-workspace": "/achat/efacture", // + /:id/workspace handled by caller
  "offre-comparison": "/aide-decision/pre-commandes", // + /:id/comparison
  "offre-counter": "/aide-decision/offres", // + /:id/counter
  "commercial-rfq-comparison": "/commercial/appel-offres", // + /:id/comparaison
  conseil: "/conseil",
  // Dermo-Conseiller — caissier guided session (D4). Distinct key from the
  // legacy `conseil` (CHATBOT) page; soft-gated by AIDE_AU_CONSEIL in-page.
  "dermo-conseiller": "/dermo-conseiller",
  equipe: "/equipe",
  parametres: "/parametres",
  abonnement: "/abonnement",
  audit: "/audit",
  support: "/support",
  moncompte: "/moncompte",
  // Fournisseur portal
  catalogue: "/portail/catalogue",
  precommandes: "/portail/precommandes",
  bl: "/portail/bl",
  // Admin — paths MUST match the routes registered in App.tsx
  comptes: "/admin/pharmacies",
  pharmacies: "/admin/pharmacies",
  "fournisseurs-admin": "/admin/fournisseurs",
  // F-2 (B2G) — the NOVERA macro-admin vertical consoles.
  "novanalyse-console": "/admin/verticals/novanalyse",
  "cabinets-console": "/admin/verticals/cabinets",
  "ailaji-console": "/admin/verticals/ailaji",
  cnas: "/admin/cnas",
  nomenclature: "/admin/produits-base",
  "modules-admin": "/admin/modules",
  licences: "/admin/licences",
  // Distinct key from the pharmacy `facturation` (→ /facturation). Used by the
  // ADMIN_PHARMAKON "Opérations" nav item; the admin billing route also has
  // hardcoded `nav: "/admin/billing"` KPI cards in AdminDashboard.
  "facturation-admin": "/admin/billing",
  // Fournisseur ERP Module 08 — supplier subscriptions (MRR/plans/promos/cohorts)
  "abonnements-fournisseurs": "/admin/abonnements-fournisseurs",
  tickets: "/admin/tickets",
  broadcast: "/admin/broadcasts",
  health: "/admin/monitor/health",
  errors: "/admin/monitor/errors",
  security: "/admin/monitor/security",
  // Explorateur audit — distinct key so it hits the admin audit explorer,
  // not the proprietaire "Journal d'audit" (which keeps page key "audit").
  "audit-explorer": "/admin/monitor/audit",
  pricing: "/admin/pricing",
  "cnas-import": "/admin/cnas/import",
  "nomenclature-import": "/admin/produits-base/import",
  "admin-notifications": "/admin/notifications",
  "admin-users": "/admin/monitor/users",
};

export const pageToPath = (page) => NAV_PAGE_TO_PATH[page] ?? "/" + page;
